import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../../config/firebase';
import { useToast } from '../../../hooks/useToast';
import {
  ArrowLeftIcon,
  PlusIcon,
  TrashIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { cloudinaryConfig } from '../../../config/cloudinary';
import RichTextEditor from '../../../components/RichTextEditor';
import 'react-quill/dist/quill.snow.css';

interface PricingFeature {
  id: string;
  text: string;
  order: number;
}

interface PricingData {
  sectionTitle: string;
  sectionSubtitle: string;
  planTitle: string;
  displayPrice: string;
  priceNote: string;
  yearlyDisplayPrice?: string; // ✨ NEW
  yearlyPriceNote?: string; // ✨ NEW
  buttonText: string;
  leftImage: string;
  features: PricingFeature[];
  enabled: boolean;
  stripePriceId?: string;
  stripeProductId?: string;
}

const PricingEditorPage: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [syncingStripe, setSyncingStripe] = useState(false);
  const [pricingData, setPricingData] = useState<PricingData>({
    sectionTitle: 'Our pricing',
    sectionSubtitle: 'Simple, transparent pricing for everyone',
    planTitle: 'Our subscription offering',
    displayPrice: 'SGD 3.00',
    priceNote: '/month',
    yearlyDisplayPrice: 'SGD 30.00', // ✨ NEW
    yearlyPriceNote: '/year', // ✨ NEW
    buttonText: 'Get Unveil Now',
    leftImage: '',
    features: [],
    enabled: true,
    stripePriceId: '',
    stripeProductId: '',
  });

  useEffect(() => {
    loadPricingData();
  }, []);

  const loadPricingData = async () => {
    try {
      setLoading(true);
      const docRef = doc(db, 'landingPageContent', 'pricing');
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        setPricingData({
          sectionTitle: data.sectionTitle || 'Our pricing',
          sectionSubtitle:
            data.sectionSubtitle || 'Simple, transparent pricing for everyone',
          planTitle: data.planTitle || 'Our subscription offering',
          displayPrice: data.displayPrice || 'SGD 3.00',
          priceNote: data.priceNote || '/month',
          yearlyDisplayPrice: data.yearlyDisplayPrice || 'SGD 30.00', // ✨ NEW
          yearlyPriceNote: data.yearlyPriceNote || '/year', // ✨ NEW
          buttonText: data.buttonText || 'Get Unveil Now',
          leftImage: data.leftImage || '',
          features: data.features || [],
          enabled: data.enabled ?? true,
          stripePriceId: data.stripePriceId || '',
          stripeProductId: data.stripeProductId || '',
        });
      }
    } catch (error) {
      console.error('Error loading pricing data:', error);
      showToast({
        message: 'Failed to load pricing data',
        type: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setPricingData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setPricingData((prev) => ({ ...prev, [name]: checked }));
  };

  const syncWithStripe = async () => {
    if (!pricingData.stripePriceId) {
      showToast({
        message: 'Please enter a Stripe Price ID first',
        type: 'error',
      });
      return;
    }

    setSyncingStripe(true);
    try {
      const { getFunctions, httpsCallable } =
        await import('firebase/functions');
      const functions = getFunctions(undefined, 'asia-southeast1');
      const getStripePrice = httpsCallable(functions, 'getStripePrice');

      const result = await getStripePrice({
        priceId: pricingData.stripePriceId,
      });

      const stripeData = result.data as any;

      const formattedPrice = `${stripeData.currency.toUpperCase()} ${(
        stripeData.unit_amount / 100
      ).toFixed(2)}`;

      let priceNote = '/month';
      if (stripeData.recurring) {
        const interval = stripeData.recurring.interval;
        const count = stripeData.recurring.interval_count;
        if (count === 1) {
          priceNote = `/${interval}`;
        } else {
          priceNote = `/${count} ${interval}s`;
        }
      }

      setPricingData((prev) => ({
        ...prev,
        displayPrice: formattedPrice,
        priceNote: priceNote,
        stripeProductId: stripeData.product,
      }));

      showToast({
        message: 'Successfully synced with Stripe',
        type: 'success',
      });
    } catch (error: any) {
      console.error('Error syncing with Stripe:', error);
      showToast({
        message:
          error.message ||
          'Failed to sync with Stripe. Make sure your Price ID is correct.',
        type: 'error',
      });
    } finally {
      setSyncingStripe(false);
    }
  };

  const uploadToCloudinary = async (file: File): Promise<string> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', cloudinaryConfig.uploadPreset);
    formData.append('folder', 'landing-page/pricing');

    const response = await fetch(
      `https://api.cloudinary.com/v1_1/${cloudinaryConfig.cloudName}/image/upload`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      throw new Error('Upload failed');
    }

    const data = await response.json();
    return data.secure_url;
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 5 * 1024 * 1024) {
      showToast({
        message: 'Image size should be less than 5MB',
        type: 'error',
      });
      return;
    }

    try {
      setUploadingImage(true);
      const imageUrl = await uploadToCloudinary(file);
      setPricingData((prev) => ({ ...prev, leftImage: imageUrl }));
      showToast({
        message: 'Image uploaded successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error uploading image:', error);
      showToast({
        message: 'Failed to upload image',
        type: 'error',
      });
    } finally {
      setUploadingImage(false);
    }
  };

  const addFeature = () => {
    const newFeature: PricingFeature = {
      id: Date.now().toString(),
      text: '',
      order: pricingData.features.length,
    };
    setPricingData((prev) => ({
      ...prev,
      features: [...prev.features, newFeature],
    }));
  };

  const updateFeature = (id: string, text: string) => {
    setPricingData((prev) => ({
      ...prev,
      features: prev.features.map((f) => (f.id === id ? { ...f, text } : f)),
    }));
  };

  const deleteFeature = (id: string) => {
    setPricingData((prev) => ({
      ...prev,
      features: prev.features.filter((f) => f.id !== id),
    }));
  };

  const moveFeature = (index: number, direction: 'up' | 'down') => {
    const newFeatures = [...pricingData.features];
    const targetIndex = direction === 'up' ? index - 1 : index + 1;

    if (targetIndex < 0 || targetIndex >= newFeatures.length) return;

    [newFeatures[index], newFeatures[targetIndex]] = [
      newFeatures[targetIndex],
      newFeatures[index],
    ];

    newFeatures.forEach((feature, idx) => {
      feature.order = idx;
    });

    setPricingData((prev) => ({ ...prev, features: newFeatures }));
  };

  const handleSave = async () => {
    if (!pricingData.sectionTitle.trim()) {
      showToast({
        message: 'Section title is required',
        type: 'error',
      });
      return;
    }

    if (!pricingData.planTitle.trim()) {
      showToast({
        message: 'Plan title is required',
        type: 'error',
      });
      return;
    }

    if (!pricingData.displayPrice.trim()) {
      showToast({
        message: 'Display price is required',
        type: 'error',
      });
      return;
    }

    try {
      setSaving(true);
      const docRef = doc(db, 'landingPageContent', 'pricing');
      await setDoc(
        docRef,
        {
          ...pricingData,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      showToast({
        message: 'Pricing section saved successfully',
        type: 'success',
      });
    } catch (error) {
      console.error('Error saving pricing section:', error);
      showToast({
        message: 'Failed to save pricing section',
        type: 'error',
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading pricing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <button
          onClick={() => navigate('/admin/landing-page')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeftIcon className="w-5 h-5 mr-2" />
          Back to Landing Page Editor
        </button>
        <h1 className="text-3xl font-bold text-gray-900">
          Edit Pricing Section
        </h1>
        <p className="text-gray-600 mt-2">
          Manage pricing display on your landing page
        </p>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
        {/* Section Header */}
        <div>
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            Section Header
          </h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section Title
              </label>
              <RichTextEditor
                value={pricingData.sectionTitle}
                onChange={(value) =>
                  setPricingData((prev) => ({ ...prev, sectionTitle: value }))
                }
                placeholder="Our pricing"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Section Subtitle
              </label>
              <RichTextEditor
                value={pricingData.sectionSubtitle}
                onChange={(value) =>
                  setPricingData((prev) => ({
                    ...prev,
                    sectionSubtitle: value,
                  }))
                }
                placeholder="Simple, transparent pricing for everyone"
              />
            </div>
          </div>
        </div>

        {/* Stripe Integration */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Stripe Integration (Optional)
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Stripe Price ID
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  name="stripePriceId"
                  value={pricingData.stripePriceId || ''}
                  onChange={handleInputChange}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="price_xxxxxxxxxxxxx"
                />
                <button
                  onClick={syncWithStripe}
                  disabled={syncingStripe || !pricingData.stripePriceId}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                >
                  <ArrowPathIcon
                    className={`h-4 w-4 ${syncingStripe ? 'animate-spin' : ''}`}
                  />
                  {syncingStripe ? 'Syncing...' : 'Sync'}
                </button>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                Enter your Stripe Price ID and click Sync to automatically fill
                in the pricing details
              </p>
            </div>
          </div>
        </div>

        {/* Plan Details */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Plan Details
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Plan Title
              </label>
              <RichTextEditor
                value={pricingData.planTitle}
                onChange={(value) =>
                  setPricingData((prev) => ({ ...prev, planTitle: value }))
                }
                placeholder="Our subscription offering"
              />
            </div>
          </div>
        </div>

        {/* Monthly Pricing */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Monthly Pricing
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Display Price
              </label>
              <input
                type="text"
                name="displayPrice"
                value={pricingData.displayPrice}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="SGD 3.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Price Note
              </label>
              <input
                type="text"
                name="priceNote"
                value={pricingData.priceNote}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="/month"
              />
            </div>
          </div>
        </div>

        {/* ✨ NEW: Yearly Pricing */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Yearly Pricing (Optional)
          </h3>
          <p className="text-sm text-gray-600 mb-4">
            If you want to show yearly pricing on the landing page, fill in
            these fields. Leave empty to hide yearly pricing.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yearly Display Price
              </label>
              <input
                type="text"
                name="yearlyDisplayPrice"
                value={pricingData.yearlyDisplayPrice || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="SGD 30.00"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Yearly Price Note
              </label>
              <input
                type="text"
                name="yearlyPriceNote"
                value={pricingData.yearlyPriceNote || ''}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="/year"
              />
            </div>
          </div>
        </div>

        {/* Features */}
        <div className="border-t pt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Features</h3>
            <button
              onClick={addFeature}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              <PlusIcon className="w-5 h-5" />
              Add Feature
            </button>
          </div>

          <div className="space-y-3">
            {pricingData.features
              .sort((a, b) => a.order - b.order)
              .map((feature, index) => (
                <div
                  key={feature.id}
                  className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg"
                >
                  <div className="flex flex-col gap-1">
                    <button
                      onClick={() => moveFeature(index, 'up')}
                      disabled={index === 0}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      ▲
                    </button>
                    <button
                      onClick={() => moveFeature(index, 'down')}
                      disabled={index === pricingData.features.length - 1}
                      className="p-1 hover:bg-gray-200 rounded disabled:opacity-30"
                    >
                      ▼
                    </button>
                  </div>
                  <input
                    type="text"
                    value={feature.text}
                    onChange={(e) => updateFeature(feature.id, e.target.value)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    placeholder="Feature description"
                  />
                  <button
                    onClick={() => deleteFeature(feature.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg"
                  >
                    <TrashIcon className="w-5 h-5" />
                  </button>
                </div>
              ))}

            {pricingData.features.length === 0 && (
              <p className="text-center text-gray-500 py-8">
                No features added yet. Click "Add Feature" to get started.
              </p>
            )}
          </div>
        </div>

        {/* Left Image */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Left Image (Phone Mockup)
          </h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Image URL
              </label>
              <input
                type="text"
                name="leftImage"
                value={pricingData.leftImage}
                onChange={handleInputChange}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="https://example.com/image.png"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Or Upload Image
              </label>
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploadingImage}
                className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 disabled:opacity-50"
              />
              {uploadingImage && (
                <p className="text-sm text-gray-600 mt-2">Uploading...</p>
              )}
            </div>
            {pricingData.leftImage && (
              <div className="mt-4">
                <img
                  src={pricingData.leftImage}
                  alt="Preview"
                  className="w-48 h-auto rounded-lg border"
                />
              </div>
            )}
          </div>
        </div>

        {/* Button Text */}
        <div className="border-t pt-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Call to Action
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Button Text
            </label>
            <input
              type="text"
              name="buttonText"
              value={pricingData.buttonText}
              onChange={handleInputChange}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Get Unveil Now"
            />
          </div>
        </div>

        {/* Visibility Toggle */}
        <div className="border-t pt-6">
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              name="enabled"
              checked={pricingData.enabled}
              onChange={handleCheckboxChange}
              className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
            />
            <span className="text-sm font-medium text-gray-700">
              Show pricing section on landing page
            </span>
          </label>
        </div>

        {/* Save Button */}
        <div className="flex justify-end gap-4 pt-6 border-t">
          <button
            onClick={() => navigate('/admin/landing-page')}
            className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PricingEditorPage;
